/**
 * Borrador de factura — `TDS-CORE` Parte 3 §1, §2 y §8 (EDITOR).
 *
 * - **No calcula importes**: los totales salen de la API (`URS §129`).
 * - Un borrador no tiene número ni registro VERI*FACTU (`URS §18`). La emisión
 *   llega en la siguiente entrega; mientras, se prepara y se guarda.
 * - **Sin "imprimir" ni "descargar" todavía**: cada vez que un borrador sale de
 *   Faktelio hay que guardar una copia inmutable (§1.1, AEAT-REF-020). Esa copia
 *   llega con el PDF; hasta entonces el borrador no sale de la aplicación.
 * - Descartar no borra: se conserva (§1.1).
 */
import { useCallback, useEffect, useState } from "react";
import {
  api,
  ESTADO_VALIDACION,
  type AnticipoDescontado,
  type CatalogoIva,
  type Cliente,
  type ControlAnulacion,
  type ControlPrevio,
  type DatosFactura,
  type ErrorApi,
  type Factura,
  type ModalidadRectificativa,
  type MotivoControlPrevio,
  type MotivoRectificativa,
  type Producto,
} from "../api/cliente";
import { t } from "../i18n/es";
import { CodigoQr } from "../ui/CodigoQr";
import { EditorLineas, LINEA_VACIA, TarjetaTotales } from "../ui/EditorLineas";
import { EditorSuplidos } from "../ui/EditorSuplidos";
import { diaLegible, importeLegible, sinCerosSobrantes } from "../ui/formato";
import { InsigniaFactura } from "../ui/Insignia";
import { AreaTexto, Aviso, Boton, Buscador, Campo, Selector, Tarjeta } from "../ui/primitivas";
import { vencimientoAlElegirCliente } from "../ui/vencimiento";
import { BloqueActividades, seguimientoDeCobro } from "./Actividades";
import { EnviarDocumento } from "./EnviarDocumento";
import { TarjetaCobros } from "./TarjetaCobros";
import { TarjetaNotaAsesoria } from "./TarjetaNotaAsesoria";
import { ConversacionConLaAsesoria } from "./ConversacionConLaAsesoria";

/** Lista L2 sin R5 (fuera de V1, DR-04) y lista L3 (Parte 4 §2.2 y §2.3). */
const MOTIVOS_RECTIFICATIVA: readonly MotivoRectificativa[] = ["R1", "R2", "R3", "R4"];
const MODALIDADES_RECTIFICATIVA: readonly ModalidadRectificativa[] = ["I", "S"];

/** Los códigos de motivo que trae un 409 VOID_BLOCKED. Ninguno se inventa. */
function codigosDeMotivos(detalles: Readonly<Record<string, unknown>>): readonly string[] {
  const lista = detalles["reasons"];
  if (!Array.isArray(lista)) return [];
  const codigos: string[] = [];
  for (const m of lista) {
    const code = (m as { readonly code?: unknown }).code;
    if (typeof code === "string") codigos.push(code);
  }
  return codigos;
}

const VACIO: DatosFactura = {
  customerId: "",
  description: null,
  operationDate: null,
  dueDate: null,
  notes: null,
  withholdingRate: null,
  withholdingReason: null,
  lines: [LINEA_VACIA],
  disbursements: [],
};

/** Un motivo del control previo en lenguaje llano. */
function textoMotivo(m: MotivoControlPrevio): string {
  if (m.code === "QUOTA_MONTH") return t.factura.cuotaMes(m.limit ?? 0);
  if (m.code === "QUOTA_YEAR") return t.factura.cuotaAnio(m.limit ?? 0);
  if (m.code === "FISCAL_VALIDATION") return t.factura.validacionFiscal[m.error ?? ""] ?? t.factura.validacionFiscalOtra;
  return t.factura.motivos[m.code] ?? t.comun.errorGenerico;
}

/** Si entre los motivos del rechazo está la autorización a la AEAT. */
function pasoDelAlta(error: ErrorApi): boolean {
  const motivos = error.detalles["reasons"];
  if (!Array.isArray(motivos)) return false;
  return motivos.some((m) => (m as { code?: unknown } | null)?.code === "REPRESENTATION_REQUIRED");
}

/**
 * Lo que fue del registro de alta ante la AEAT.
 *
 * Falla del lado de «todavía no»: sin dato, o con un estado que no se
 * reconoce, NO se dice que esté enviada. Dar por registrada una factura que no
 * lo está es lo peor que puede hacer esta pantalla.
 */
function AvisoRemision({ envio }: { envio: Factura["aeatSubmission"] | undefined }) {
  /*
    `undefined` no es un caso teórico: la web y el Worker se despliegan por
    separado, así que durante unos minutos una web nueva puede estar hablando
    con una API que todavía no manda este campo. Entonces no se sabe, y se
    dice; nunca se da por enviada.
  */
  if (envio === null || envio === undefined) return <Aviso tono="aviso">{t.factura.envioDesconocido}</Aviso>;
  if (envio.status === "PENDING") return <Aviso tono="info">{t.factura.pendienteEnvio}</Aviso>;
  if (envio.status === "REJECTED") return <Aviso tono="error">{t.factura.rechazadaAeat}</Aviso>;
  if (envio.csv === null) return <Aviso tono="exito">{t.factura.enviadaAeatSinCsv}</Aviso>;
  // Aceptada con defecto: se anotó, pero hay que subsanarla (art. 16.4).
  if (envio.aeatState === "AceptadoConErrores") return <Aviso tono="aviso">{t.factura.aceptadaConErrores(envio.csv)}</Aviso>;
  return <Aviso tono="exito">{t.factura.enviadaAeat(envio.csv)}</Aviso>;
}

/**
 * Cabecera de la ficha: lo que se pregunta al abrir una factura —cuál es, de
 * cuándo, de quién, cuánto y en qué estado— en una sola línea de lectura.
 *
 * Antes lo primero que ocupaba la ficha era el QR, que es lo que menos se mira
 * (lo lee una cámara, no una persona), y el importe no aparecía hasta el final.
 *
 * Las DOS cifras van etiquetadas. Poner una sola sin decir cuál es sería el
 * error de `OD-0029 D-06`: el total de la factura y lo que el cliente paga son
 * importes distintos en cuanto hay retención, y confundirlos descuadra un cobro.
 */
function CabeceraFactura({
  factura,
  editable,
  onAbrirCliente,
}: {
  factura: Factura;
  editable: boolean;
  onAbrirCliente: (id: string) => void;
}) {
  const totals = factura.totals;
  return (
    <div className="cabecera-documento">
      <div className="cabecera-documento-datos">
        {factura.issueDate !== null ? <p className="ayuda-neutra">{t.factura.fechaExpedicion(diaLegible(factura.issueDate))}</p> : null}
        {factura.customer !== null && !editable ? (
          <p>
            <button type="button" className="enlace-en-texto" onClick={() => onAbrirCliente(factura.customerId)}>
              {factura.customer.displayName}
            </button>
          </p>
        ) : null}
        {factura.status === "ISSUED" ? (
          <p>
            <a href={`/api/v1/invoices/${factura.id}/pdf`} target="_blank" rel="noopener noreferrer">
              {t.factura.verDocumento}
            </a>
          </p>
        ) : null}
      </div>
      <div className="cabecera-documento-cifra">
        <InsigniaFactura estado={factura.documentState} respaldo={factura.status} />
        {totals !== null ? (
          <>
            <p className="cabecera-documento-total">{importeLegible(totals.invoiceTotal)} €</p>
            <p className="ayuda-neutra">{t.presupuesto.totalFactura}</p>
            {totals.withholdingRate !== null || totals.disbursementsTotal !== undefined ? (
              <p className="ayuda-neutra">{t.factura.cabeceraClientePaga(importeLegible(totals.amountToCollect))}</p>
            ) : null}
          </>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Lo que una factura FINAL descuenta de sus anticipos (`OD-0053 D-03`): una
 * fila por anticipo y grupo, con la base que ya se facturó. Lo calcula la API.
 */
function ListaDescuentos({ descuentos }: { descuentos: readonly AnticipoDescontado[] }) {
  return (
    <>
      <h3>{t.anticipos.descuentosTitulo}</h3>
      <ul>
        {descuentos.map((d, i) => (
          <li key={i}>{t.anticipos.descuento(d.advanceNumber, importeLegible(d.base))}</li>
        ))}
      </ul>
    </>
  );
}

/** El día de hoy en Madrid, AAAA-MM-DD: sólo para proponer el vencimiento; lo valida la API. */
function hoy(): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: "Europe/Madrid" }).format(new Date());
}

function etiquetaCliente(c: Cliente): string {
  return c.taxIdNormalized === null ? c.displayName : `${c.displayName} · ${c.taxIdNormalized}`;
}

function datosDe(f: Factura): DatosFactura {
  return {
    customerId: f.customerId,
    description: f.description,
    operationDate: f.operationDate,
    dueDate: f.dueDate,
    notes: f.notes,
    withholdingRate: f.withholdingRate,
    withholdingReason: f.withholdingReason,
    lines:
      f.lines.length === 0
        ? [LINEA_VACIA]
        : f.lines.map((l) => ({
            ...l,
            quantity: sinCerosSobrantes(l.quantity),
            unitPrice: sinCerosSobrantes(l.unitPrice),
            discount: l.discount.kind === "NONE" ? l.discount : { ...l.discount, value: sinCerosSobrantes(l.discount.value) },
          })),
    // Guardados, ya los confirmó: la casilla vuelve marcada (OD-0052 D-01).
    disbursements: (f.disbursements ?? []).map((s) => ({
      description: s.description,
      amount: s.amount,
      paidTo: s.paidTo ?? "",
      reference: s.reference ?? "",
      onBehalfConfirmed: true,
    })),
  };
}

/** "" -> null: la API distingue "sin dato" de texto vacío. */
function limpiar(d: DatosFactura): DatosFactura {
  const n = (v: string | null): string | null => (v === null || v.trim() === "" ? null : v);
  return {
    ...d,
    description: n(d.description),
    operationDate: n(d.operationDate),
    dueDate: n(d.dueDate),
    notes: n(d.notes),
    lines: d.lines.map((l) => ({
      ...l,
      vatRate: l.vatTreatment === "SUJETA_NO_EXENTA" ? l.vatRate : null,
      vatCause: l.vatTreatment === "SUJETA_NO_EXENTA" ? null : l.vatCause,
    })),
    disbursements: d.disbursements.map((s) => ({ ...s, paidTo: s.paidTo.trim(), reference: s.reference.trim() })),
  };
}

export function PantallaFactura({
  id,
  hayConexion,
  onVolver,
  onAbrir,
  onIrAEmisor,
  onAbrirPuerta,
  onAbrirCliente,
  onSesionCaducada,
}: {
  /** `null` = borrador nuevo. */
  id: string | null;
  hayConexion: boolean;
  onVolver: () => void;
  onAbrir: (id: string) => void;
  onIrAEmisor: () => void;
  /**
   * Abre el diálogo del alta (`OD-0039` E-11). La autorización y el segundo
   * factor no se arreglan en una pantalla suelta: son un trámite de tres
   * pasos, y ese trámite vive en ese diálogo.
   */
  onAbrirPuerta: () => void;
  onAbrirCliente: (id: string) => void;
  onSesionCaducada: () => void;
}) {
  const [factura, setFactura] = useState<Factura | null>(null);
  const [datos, setDatos] = useState<DatosFactura>(VACIO);
  const [clientes, setClientes] = useState<readonly Cliente[]>([]);
  const [productos, setProductos] = useState<readonly Producto[]>([]);
  const [catalogo, setCatalogo] = useState<CatalogoIva | null>(null);
  const [cargando, setCargando] = useState(true);
  const [errores, setErrores] = useState<Readonly<Record<string, string>>>({});
  const [erroresLinea, setErroresLinea] = useState<Readonly<Record<string, Readonly<Record<string, string>>>>>({});
  const [mensaje, setMensaje] = useState<{ tono: "exito" | "error" | "aviso" | "info"; texto: string } | null>(null);
  const [ocupado, setOcupado] = useState(false);
  const [descartando, setDescartando] = useState(false);
  const [confirmandoEmision, setConfirmandoEmision] = useState(false);
  const [anulando, setAnulando] = useState(false);
  const [rectificando, setRectificando] = useState(false);
  const [motivoRectificativa, setMotivoRectificativa] = useState<MotivoRectificativa>("R1");
  const [modalidadRectificativa, setModalidadRectificativa] = useState<ModalidadRectificativa>("I");
  const [fueraDePlazo, setFueraDePlazo] = useState(false);
  // OD-0050-ENMIENDA-01 E-04: lo que dice la tarjeta de cobros, para el bloque de tareas.
  const [cuentaCobro, setCuentaCobro] = useState<{ readonly cobradaEntera: boolean; readonly trasApuntar: boolean } | null>(null);
  const alCambiarCuenta = useCallback((c: { readonly cobradaEntera: boolean; readonly trasApuntar: boolean }) => setCuentaCobro(c), []);
  const [motivosRectificar, setMotivosRectificar] = useState<readonly string[]>([]);
  const [motivoAnulacion, setMotivoAnulacion] = useState("");
  /**
   * Qué impide anular, preguntado a la API. `null` mientras no se sabe.
   *
   * Antes esto era una lista que sólo se rellenaba DESPUÉS de intentarlo: se
   * escribía el motivo, se pulsaba, y entonces llegaba el «no». Ahora se
   * pregunta al abrir, como con la emisión.
   */
  const [controlAnulacion, setControlAnulacion] = useState<ControlAnulacion | null>(null);
  const [controlPrevio, setControlPrevio] = useState<ControlPrevio | null>(null);
  const [clave] = useState(() => crypto.randomUUID());
  const [busquedaCliente, setBusquedaCliente] = useState("");
  /** El vencimiento que propuso la pantalla (`OD-0051 D-07`), mientras siga sin tocar. */
  const [vencimientoSugerido, setVencimientoSugerido] = useState<string | null>(null);
  const [busquedaProducto, setBusquedaProducto] = useState<Readonly<Record<number, string>>>({});
  /** `OD-0052 D-06 B`: lo dice la API. Mientras no se sabe, no se ofrece. */
  const [suplidosIncluidos, setSuplidosIncluidos] = useState(false);
  /** Para el asunto del correo de «Enviar»: el nombre de hoy, no un dato fiscal. */
  const [nombreEmisor, setNombreEmisor] = useState("");

  useEffect(() => {
    void (async () => {
      const [cat, prods, cli, f, uso, emi] = await Promise.all([
        api.catalogoIva(),
        api.listarProductos(),
        api.listarClientes("ACTIVE"),
        id === null ? Promise.resolve(null) : api.obtenerFactura(id),
        api.usoFacturas(),
        api.obtenerEmisor(),
      ]);
      setCargando(false);
      if (emi.ok && emi.datos.emisor !== null) setNombreEmisor(emi.datos.emisor.legalName);
      setSuplidosIncluidos(uso.ok && uso.datos.disbursementsIncluded === true);
      if (cat.ok) setCatalogo(cat.datos);
      if (prods.ok) setProductos(prods.datos.filter((p) => p.active));
      if (cli.ok) setClientes(cli.datos);
      if (f === null) return;
      if (f.ok) {
        setFactura(f.datos);
        setDatos(datosDe(f.datos));
        const suyo = (cli.ok ? cli.datos : []).find((c) => c.id === f.datos.customerId);
        setBusquedaCliente(suyo === undefined ? (f.datos.customer?.displayName ?? "") : etiquetaCliente(suyo));
        const lista = prods.ok ? prods.datos : [];
        setBusquedaProducto(
          Object.fromEntries(
            f.datos.lines.map((l, i) => {
              const p = lista.find((x) => x.id === l.productId);
              return [i, p === undefined ? "" : `${p.code} · ${p.description}`];
            }),
          ),
        );
      } else if (f.estado === 401) onSesionCaducada();
      else setMensaje({ tono: "error", texto: f.estado === 404 ? t.factura.noEncontrada : t.comun.errorGenerico });
    })();
  }, [id, onSesionCaducada]);

  // Qué falta para emitir: se pregunta a la API cada vez que cambia lo guardado.
  const idGuardado = factura?.id ?? null;
  const versionGuardada = factura?.updatedAt ?? null;
  const estadoGuardado = factura?.status ?? null;
  useEffect(() => {
    if (idGuardado === null || estadoGuardado !== "DRAFT") {
      setControlPrevio(null);
      return;
    }
    void (async () => {
      const r = await api.controlPrevioFactura(idGuardado);
      setControlPrevio(r.ok ? r.datos : null);
    })();
  }, [idGuardado, versionGuardada, estadoGuardado]);

  // Y qué impide ANULAR, que sólo tiene sentido en una factura emitida.
  useEffect(() => {
    if (idGuardado === null || estadoGuardado !== "ISSUED") {
      setControlAnulacion(null);
      return;
    }
    void (async () => {
      const r = await api.controlPrevioAnulacion(idGuardado);
      setControlAnulacion(r.ok ? r.datos : null);
    })();
  }, [idGuardado, versionGuardada, estadoGuardado]);

  const editable = factura === null || factura.status === "DRAFT";
  // OD-0053 D-02 A: un anticipo sale del presupuesto. En su borrador sólo se
  // toca la cabecera; para otro porcentaje se descarta y se hace otro.
  const anticipo = factura?.advance ?? null;
  const descuentos = factura?.advanceDeductions ?? [];
  const errorDe = (campo: string): string | null =>
    errores[campo] === undefined
      ? null
      : (t.factura.errores[errores[campo]] ?? t.presupuesto.errores[errores[campo]] ?? t.anticipos.errores[errores[campo]] ?? t.comun.errorGenerico);
  const errorLinea = (i: number, campo: string): string | null => {
    const code = erroresLinea[String(i)]?.[campo];
    return code === undefined ? null : (t.presupuesto.errores[code] ?? t.comun.errorGenerico);
  };

  function limpiarAvisos(): void {
    setMensaje(null);
    setErrores({});
    setErroresLinea({});
  }

  function rechazo(r: { estado: number; error: ErrorApi | null }): void {
    if (r.estado === 401) return onSesionCaducada();
    if (r.estado === 403) return setMensaje({ tono: "aviso", texto: t.factura.sinPermiso });
    // 402: hoy sólo lo da guardar suplidos fuera de Business (OD-0052 D-06 B).
    if (r.estado === 402) return setMensaje({ tono: "aviso", texto: t.suplidos.soloBusiness });
    if (r.estado === ESTADO_VALIDACION && r.error !== null) {
      setErrores(r.error.campos);
      setErroresLinea(r.error.lineas);
      return setMensaje({ tono: "error", texto: r.error.message });
    }
    if (r.estado === 409 && r.error?.motivo === "NOT_DRAFT") return setMensaje({ tono: "aviso", texto: t.factura.noEditable });
    if (r.estado === 409 && r.error?.motivo === "CONCURRENT_UPDATE") return setMensaje({ tono: "aviso", texto: t.factura.conflicto });
    if (r.estado === 409 && r.error !== null) return setMensaje({ tono: "aviso", texto: r.error.message });
    // 429: la API ya explica el límite y qué hacer (URS §110).
    if (r.estado === 429 && r.error !== null) return setMensaje({ tono: "aviso", texto: `${r.error.message} ${r.error.action ?? ""}`.trim() });
    setMensaje({ tono: "error", texto: r.estado === 0 ? t.comun.sinRed : t.comun.errorGenerico });
  }

  async function guardar(): Promise<void> {
    limpiarAvisos();
    setOcupado(true);
    const cuerpo = limpiar(datos);
    const r = factura === null ? await api.crearFactura(cuerpo, clave) : await api.editarFactura(factura.id, cuerpo);
    setOcupado(false);
    if (!r.ok) return rechazo(r);
    const eraAlta = factura === null;
    setFactura(r.datos);
    setDatos(datosDe(r.datos));
    if (eraAlta) return onAbrir(r.datos.id);
    setMensaje({ tono: "exito", texto: t.factura.guardado });
  }

  async function emitir(): Promise<void> {
    if (factura === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.emitirFactura(factura.id);
    setOcupado(false);
    setConfirmandoEmision(false);
    if (!r.ok) {
      /*
        La API vuelve a comprobarlo todo al emitir, y puede decir que no
        aunque el control previo que se pidió al abrir dijera que sí: basta
        con que en otra pestaña se haya revocado la autorización o desactivado
        el segundo factor. Si el motivo es uno de los dos pasos del alta, se
        abre el diálogo del trámite en vez de dejar un error y nada que hacer.
      */
      if (r.estado === 409 && r.error?.motivo === "PREFLIGHT_BLOCKED" && pasoDelAlta(r.error)) onAbrirPuerta();
      // Y se vuelve a preguntar qué falta, para que la lista deje de mentir.
      const c = await api.controlPrevioFactura(factura.id);
      setControlPrevio(c.ok ? c.datos : null);
      return rechazo(r);
    }
    setFactura(r.datos);
    setMensaje({ tono: "exito", texto: t.factura.emitida });
  }

  /**
   * Anular (TDS-FISCAL Parte 4 §3). La factura no se borra ni se edita: la API
   * encadena un registro de anulacion y la deja anulada.
   */
  async function anular(): Promise<void> {
    if (factura === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.anularFactura(factura.id, motivoAnulacion.trim());
    setOcupado(false);
    if (!r.ok) {
      /*
        La API puede decir que no aunque el control previo dijera que sí: entre
        una cosa y otra alguien pudo crear una rectificativa en otra pestaña.
        Se vuelve a preguntar, que es lo que deja la pantalla contando la
        verdad, en vez de apuntar los motivos de este intento y ya.
      */
      if (r.estado === 409 && r.error?.motivo === "VOID_BLOCKED") {
        const c = await api.controlPrevioAnulacion(factura.id);
        setControlAnulacion(c.ok ? c.datos : null);
        return;
      }
      return rechazo(r);
    }
    setAnulando(false);
    setMotivoAnulacion("");
    setFactura(r.datos);
    setMensaje({ tono: "info", texto: t.factura.anulada });
  }

  /**
   * Preparar una rectificativa (`TDS-FISCAL` Parte 4 §2, `DR4-08`). No toca esta
   * factura: crea un BORRADOR nuevo, en serie R, y se abre para escribirlo.
   *
   * El aviso de los cuatro años (art. 15.3) llega como motivo del control
   * previo y se responde CONFIRMANDO, no desbloqueando nada a mano: la segunda
   * llamada lleva `confirmOutOfTime` y la API vuelve a decidir.
   */
  async function rectificar(confirmando = false): Promise<void> {
    if (factura === null) return;
    limpiarAvisos();
    setMotivosRectificar([]);
    setOcupado(true);
    const r = await api.rectificarFactura(factura.id, motivoRectificativa, modalidadRectificativa, confirmando);
    setOcupado(false);
    if (!r.ok) {
      if (r.estado === 409 && r.error?.motivo === "RECTIFICATION_BLOCKED") {
        const codigos = codigosDeMotivos(r.error.detalles);
        // El plazo no bloquea: pregunta (DR4-04 B).
        if (codigos.length === 1 && codigos[0] === "OUT_OF_TIME_CONFIRMATION_REQUIRED") {
          setFueraDePlazo(true);
          return;
        }
        setMotivosRectificar(codigos);
        return;
      }
      return rechazo(r);
    }
    setRectificando(false);
    setFueraDePlazo(false);
    onAbrir(r.datos.id);
  }

  async function descartar(): Promise<void> {
    if (factura === null) return;
    limpiarAvisos();
    setOcupado(true);
    const r = await api.descartarFactura(factura.id);
    setOcupado(false);
    setDescartando(false);
    if (!r.ok) return rechazo(r);
    setFactura(r.datos);
    setMensaje({ tono: "info", texto: t.factura.descartado });
  }

  if (cargando) {
    return (
      <Tarjeta titulo={t.factura.tituloNuevo}>
        <p>{t.facturas.cargando}</p>
      </Tarjeta>
    );
  }

  const titulo =
    factura === null
      ? t.factura.tituloNuevo
      : factura.number !== null
        ? t.factura.titulo(factura.number)
        : factura.status === "DISCARDED"
          ? t.factura.tituloDescartado
          : t.factura.tituloBorrador;
  const opcionesCliente = clientes.map((c) => ({ id: c.id, texto: etiquetaCliente(c), claves: [c.displayName, c.taxIdNormalized] }));
  // OD-0062 E-03: si el vencimiento propuesto sale del plazo de su ficha, la ayuda lo dice.
  const plazoDelCliente = clientes.find((c) => c.id === datos.customerId)?.paymentTermsDays ?? null;
  const ayudaPropuesto = plazoDelCliente === null ? t.factura.ayudaDueDatePropuesto : t.factura.ayudaDueDatePlazo(plazoDelCliente);
  const totals = factura?.totals ?? null;
  const sugerida = factura?.suggestedDescription ?? null;

  return (
    <>
      <Boton variante="secundario" onClick={onVolver}>
        {t.factura.volver}
      </Boton>

      <Tarjeta titulo={titulo}>
        {factura !== null ? <CabeceraFactura factura={factura} editable={editable} onAbrirCliente={onAbrirCliente} /> : null}
        {factura?.sourceQuoteNumber ? <p className="ayuda-neutra">{t.factura.desdePresupuesto(factura.sourceQuoteNumber)}</p> : null}
        {/* Una rectificativa se escribe distinto segun la modalidad, y quien la
            abre manana tiene que saber cual eligio sin buscarlo (DR4-08). */}
        {/* URS §28: la original no cambia, pero tiene que DECIR que ya tiene
            rectificativa, y dejar abrirla sin buscarla en el listado. */}
        {factura !== null && factura.rectifiedBy.length > 0 ? (
          <Aviso tono="aviso" titulo={t.factura.rectificadaTitulo}>
            <>
              {t.factura.rectificadaDetalle}{" "}
              {factura.rectifiedBy.map((r, i) => (
                <span key={r.id}>
                  {i > 0 ? ", " : ""}
                  <button type="button" className="enlace-en-texto" onClick={() => onAbrir(r.id)}>
                    {r.number ?? t.facturas.sinNumero}
                  </button>
                </span>
              ))}
            </>
          </Aviso>
        ) : null}
        {factura?.rectification != null ? (
          <Aviso tono="info" titulo={t.factura.rectificativaDe(factura.rectification.rectifiesNumber ?? "")}>
            {factura.rectification.kind === "I" ? t.factura.rectificativaAyudaI : t.factura.rectificativaAyudaS}
          </Aviso>
        ) : null}
        {anticipo !== null ? (
          <Aviso tono={editable ? "aviso" : "info"} titulo={t.anticipos.borradorTitulo(anticipo.percent, anticipo.quoteNumber)}>
            {editable ? t.anticipos.emitirYa : null}
          </Aviso>
        ) : null}
        {editable ? <Aviso tono="info">{t.factura.sinEmisionTodavia}</Aviso> : null}
        {factura?.status === "DISCARDED" ? <Aviso tono="info">{t.factura.descartadoDetalle}</Aviso> : null}
        {factura?.status === "VOIDED" ? (
          <>
            <Aviso tono="aviso">
              {factura.voidedAt === null || factura.voidReason === null
                ? t.factura.anulada
                : t.factura.anuladaDetalle(factura.voidedAt.slice(0, 10), factura.voidReason)}
            </Aviso>
            {/*
              Los DOS registros. Anular no borra nada: la AEAT tiene el alta y,
              encadenado, un registro de anulación. Esta pantalla no enseñaba
              ninguno de los dos —el bloque del QR sólo se dibuja en una factura
              ISSUED—, así que con una anulada no había forma de saber si la
              Agencia se había enterado. Y mientras la anulación no salga, para
              la AEAT esa factura sigue viva.
            */}
            <dl className="envios-registro">
              <dt>{t.factura.envioAlta}</dt>
              <dd>
                <AvisoRemision envio={factura.aeatSubmission} />
              </dd>
              <dt>{t.factura.envioAnulacion}</dt>
              <dd>
                <AvisoRemision envio={factura.voidSubmission} />
              </dd>
            </dl>
          </>
        ) : null}
        {clientes.length === 0 && editable ? <Aviso tono="aviso">{t.presupuesto.sinClientes}</Aviso> : null}

        {editable ? (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void guardar();
            }}
            noValidate
          >
            {anticipo !== null ? (
              <p>{factura?.customer?.displayName ?? busquedaCliente}</p>
            ) : (
              <Buscador
                id="fac-cliente"
                etiqueta={t.presupuesto.cliente}
                texto={busquedaCliente}
                opciones={opcionesCliente}
                marcador={t.presupuesto.eligeCliente}
                ayuda={t.factura.ayudaCliente}
                onTexto={setBusquedaCliente}
                onElegir={(cid) => {
                  const elegido = clientes.find((c) => c.id === cid) ?? null;
                  const v = vencimientoAlElegirCliente({ actual: datos.dueDate, propuestoAntes: vencimientoSugerido, cliente: elegido, hoy: hoy() });
                  setVencimientoSugerido(v.propuesto);
                  setDatos((d) => ({ ...d, customerId: cid ?? "", dueDate: v.dueDate }));
                }}
                error={(busquedaCliente.trim() !== "" && datos.customerId === "" ? t.presupuesto.clienteNoElegido : null) ?? errorDe("customerId")}
              />
            )}

            <AreaTexto
              id="fac-descripcion"
              etiqueta={t.factura.descripcion}
              valor={datos.description ?? ""}
              onChange={(v) => setDatos((d) => ({ ...d, description: v }))}
              ayuda={t.factura.ayudaDescripcion}
              filas={3}
              error={errorDe("description")}
            />
            {sugerida !== null && (datos.description ?? "").trim() === "" ? (
              <div className="sugerencia">
                <p className="ayuda-neutra">{t.factura.propuesta(sugerida)}</p>
                <Boton variante="secundario" onClick={() => setDatos((d) => ({ ...d, description: sugerida }))}>
                  {t.factura.usarPropuesta}
                </Boton>
              </div>
            ) : null}

            <div className="fila-campos">
              <Campo
                id="fac-operacion"
                etiqueta={anticipo !== null ? t.anticipos.fechaCobro : t.factura.operationDate}
                valor={datos.operationDate ?? ""}
                onChange={(v) => setDatos((d) => ({ ...d, operationDate: v }))}
                ayuda={anticipo !== null ? t.anticipos.ayudaFechaCobro : t.factura.ayudaOperationDate}
                tipo="date"
                autoComplete="off"
                requerido={false}
                error={errorDe("operationDate")}
              />
              <Campo
                id="fac-vencimiento"
                etiqueta={t.factura.dueDate}
                valor={datos.dueDate ?? ""}
                onChange={(v) => setDatos((d) => ({ ...d, dueDate: v }))}
                ayuda={vencimientoSugerido !== null && datos.dueDate === vencimientoSugerido ? ayudaPropuesto : t.factura.ayudaDueDate}
                tipo="date"
                autoComplete="off"
                requerido={false}
                error={errorDe("dueDate")}
              />
            </div>

            {anticipo !== null ? (
              <>
                <h3>{t.presupuesto.lineas}</h3>
                <ul>
                  {datos.lines.map((l, i) => (
                    <li key={i}>{t.factura.lineaLectura(l.description, sinCerosSobrantes(l.quantity))}</li>
                  ))}
                </ul>
                <p className="ayuda-neutra">{t.anticipos.lineasBloqueadas}</p>
              </>
            ) : (
              <EditorLineas
                prefijo="fac"
                datos={datos}
                setDatos={setDatos}
                catalogo={catalogo}
                productos={productos}
                busquedaProducto={busquedaProducto}
                setBusquedaProducto={setBusquedaProducto}
                totals={totals}
                sugerencia={factura?.withholdingSuggestion?.suggestedRate ?? null}
                pendienteRetencion={factura?.pendingWithholdingDecision === true}
                errorDe={errorDe}
                errorLinea={errorLinea}
              />
            )}

            {descuentos.length > 0 ? <ListaDescuentos descuentos={descuentos} /> : null}

            {/*
              OD-0052: fuera de las lineas, porque no son una. Se ofrecen si el
              plan los incluye, y se ensenan siempre que el borrador ya tenga
              alguno: quien baja de plan tiene que poder quitarlos. Nunca en una
              rectificativa (D-07).
            */}
            {factura?.rectification == null && anticipo === null && (suplidosIncluidos || datos.disbursements.length > 0) ? (
              <EditorSuplidos
                prefijo="fac"
                suplidos={datos.disbursements}
                onCambio={(disbursements) => setDatos((d) => ({ ...d, disbursements }))}
                errores={errores}
              />
            ) : null}

            {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
            {!hayConexion ? <Aviso tono="aviso">{t.comun.sinRed}</Aviso> : null}
            <div className="acciones">
              <Boton tipo="submit" deshabilitado={ocupado || !hayConexion}>
                {ocupado ? t.presupuesto.guardando : t.factura.guardar}
              </Boton>
            </div>
          </form>
        ) : (
          <>
            {mensaje !== null ? <Aviso tono={mensaje.tono}>{mensaje.texto}</Aviso> : null}
            {factura !== null ? (
              <dl className="datos-lectura">
                <dt>{t.factura.descripcion}</dt>
                <dd>{factura.description ?? "—"}</dd>
                <dt>{t.presupuesto.lineas}</dt>
                <dd>
                  <ul>
                    {factura.lines.map((l, i) => (
                      <li key={i}>{t.factura.lineaLectura(l.description, sinCerosSobrantes(l.quantity))}</li>
                    ))}
                  </ul>
                </dd>
                {descuentos.length > 0 ? (
                  <>
                    <dt>{t.anticipos.descuentosTitulo}</dt>
                    <dd>
                      <ul>
                        {descuentos.map((d, i) => (
                          <li key={i}>{t.anticipos.descuento(d.advanceNumber, importeLegible(d.base))}</li>
                        ))}
                      </ul>
                    </dd>
                  </>
                ) : null}
                {(factura.disbursements ?? []).length > 0 ? (
                  <>
                    <dt>{t.suplidos.titulo}</dt>
                    <dd>
                      <ul>
                        {(factura.disbursements ?? []).map((s, i) => (
                          <li key={i}>{t.suplidos.lectura(s.description, importeLegible(s.amount))}</li>
                        ))}
                      </ul>
                    </dd>
                  </>
                ) : null}
              </dl>
            ) : null}
          </>
        )}
      </Tarjeta>

      {totals !== null ? (
        <TarjetaTotales totals={totals} />
      ) : factura !== null ? (
        <Tarjeta titulo={t.presupuesto.totalesTitulo}>
          <p className="ayuda-neutra">{t.presupuesto.sinTotales}</p>
        </Tarjeta>
      ) : null}

      {/* «Enviar»: el PDF de la emitida, desde el correo del usuario. Una
          anulada no se le manda a nadie. */}
      {factura !== null && factura.status === "ISSUED" && factura.number !== null ? (
        <Tarjeta titulo={t.enviarDocumento.titulo}>
          <EnviarDocumento
            documento="invoices"
            id={factura.id}
            asunto={t.enviarDocumento.asuntoFactura(factura.number, nombreEmisor)}
            cuerpo={t.enviarDocumento.cuerpoFactura(factura.number, nombreEmisor)}
            correoCliente={clientes.find((c) => c.id === factura.customerId)?.email ?? null}
            telefonoCliente={clientes.find((c) => c.id === factura.customerId)?.phone ?? null}
            paisCliente={clientes.find((c) => c.id === factura.customerId)?.country ?? "ES"}
            hayConexion={hayConexion}
            onSesionCaducada={onSesionCaducada}
          />
        </Tarjeta>
      ) : null}

      {/* La verificación ante la AEAT, en su propio bloque: el QR lo lee una
          cámara, no una persona, así que no tiene por qué abrir la ficha. */}
      {factura !== null && factura.status === "ISSUED" && factura.qrUrl !== null ? (
        <Tarjeta titulo={t.factura.verificacionTitulo}>
          <div className="factura-emitida">
            <div className="qr-tributario">
              <p>{t.factura.qrTitulo}</p>
              <CodigoQr contenido={factura.qrUrl} etiqueta={t.factura.qrEtiqueta} />
              <p className="ayuda-neutra">{t.factura.qrFrase}</p>
            </div>
            <div>
              {/*
                Lo que la API diga del registro, no una frase fija. Esto decia
                «Pendiente de envio» a TODA factura emitida, incluso a las que
                la AEAT ya habia aceptado con su justificante: el usuario veia
                un aviso que contradecia la realidad, y no tenia forma de
                saber cual de las dos cosas era cierta.
              */}
              <AvisoRemision envio={factura.aeatSubmission} />
            </div>
          </div>
        </Tarjeta>
      ) : null}

      {/* Los cobros (OD-0040 D-04): un bloque de la ficha, no una seccion del
          menu. Solo de las emitidas y las anuladas, que conservan los suyos. */}
      {factura !== null && (factura.status === "ISSUED" || factura.status === "VOIDED") ? (
        <TarjetaCobros facturaId={factura.id} hayConexion={hayConexion} onCuenta={alCambiarCuenta} onSesionCaducada={onSesionCaducada} />
      ) : null}

      {/* OD-0067 D-02: lo que el cliente le dice a su gestoria de ESTA factura.
          Aparte de la factura, que no se toca: por eso cabe en una emitida. */}
      {factura !== null && (factura.status === "ISSUED" || factura.status === "VOIDED") ? (
        <TarjetaNotaAsesoria key={factura.id} documento="factura" id={factura.id} hayConexion={hayConexion} onSesionCaducada={onSesionCaducada} />
      ) : null}
      {/* OD-0067 D-05: lo que se habla con la gestoria de ESTA factura. Sin plan,
          rol o gestoria con acceso, no se pinta. */}
      {factura !== null && (factura.status === "ISSUED" || factura.status === "VOIDED") && factura.number !== null ? (
        <ConversacionConLaAsesoria
          key={`conversacion-${factura.id}`}
          organizationId={null}
          documento={{ tipo: "factura", id: factura.id, etiqueta: factura.number }}
          hayConexion={hayConexion}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}

      {/* OD-0050-ENMIENDA-01: sus tareas (solo Business; fuera, no se pinta).
          El seguimiento del cobro se ofrece mientras quede algo por cobrar, y
          al quedar cobrada se PROPONE cerrar lo pendiente. */}
      {factura !== null && factura.status !== "DISCARDED" ? (
        <BloqueActividades
          key={factura.id}
          facturaId={factura.id}
          seguimiento={
            factura.status === "ISSUED" && factura.number !== null && cuentaCobro?.cobradaEntera !== true
              ? seguimientoDeCobro(factura.number)
              : null
          }
          cobrada={cuentaCobro?.cobradaEntera === true && cuentaCobro.trasApuntar}
          onSesionCaducada={onSesionCaducada}
        />
      ) : null}

      {controlPrevio !== null && factura !== null ? (
        <Tarjeta titulo={t.factura.paraEmitirTitulo}>
          {controlPrevio.outcome === "ALLOW_ISSUE" ? (
            <>
              <p className="ayuda-neutra">{t.factura.emitirDetalle}</p>
              {!confirmandoEmision ? (
                <Boton onClick={() => setConfirmandoEmision(true)} deshabilitado={ocupado || !hayConexion}>
                  {t.factura.emitir}
                </Boton>
              ) : (
                <div className="acciones">
                  <Boton onClick={() => void emitir()} deshabilitado={ocupado || !hayConexion}>
                    {ocupado ? t.factura.emitiendo : t.factura.confirmarEmitir}
                  </Boton>
                  <Boton variante="secundario" onClick={() => setConfirmandoEmision(false)}>
                    {t.presupuesto.cancelarAccion}
                  </Boton>
                </div>
              )}
            </>
          ) : (
            <ul className="lista-pendientes">
              {controlPrevio.reasons.map((m) => (
                <li key={`${m.code}-${m.error ?? ""}`}>
                  <span>{textoMotivo(m)}</span>
                  {m.code === "ISSUER_MISSING" || m.code === "ISSUER_NOT_CONFIRMED" ? (
                    <Boton variante="secundario" onClick={onIrAEmisor}>
                      {t.factura.irADatosFiscales}
                    </Boton>
                  ) : null}
                  {/*
                    La autorización. Antes esto era una línea de texto que
                    mandaba a Configuración, y allí no está el trámite entero:
                    está aquí al lado, en el diálogo de los tres pasos.
                  */}
                  {m.code === "REPRESENTATION_REQUIRED" ? (
                    <Boton variante="secundario" onClick={onAbrirPuerta}>
                      {t.factura.verQueFalta}
                    </Boton>
                  ) : null}
                  {/*
                    D3-a: el boton de abrir el cliente NO sale con
                    CUSTOMER_TAX_ID_NOT_SPANISH, y es deliberado. Ahi la ficha
                    del cliente no arregla nada —el limite es de alcance, no
                    del dato—, y ofrecer el boton manda a alguien a editar una
                    ficha que ya esta bien.
                  */}
                  {m.code === "CUSTOMER_TAX_ID_REQUIRED" || m.code === "CUSTOMER_ARCHIVED" ? (
                    <Boton variante="secundario" onClick={() => onAbrirCliente(factura.customerId)}>
                      {t.factura.abrirCliente}
                    </Boton>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
          <p className="ayuda-neutra">{t.factura.paraEmitirDetalle}</p>
        </Tarjeta>
      ) : null}

      {/*
        Rectificar, anular y descartar dejan de ocupar la ficha entera. Son
        acciones excepcionales —y dos de ellas irreversibles—, así que se piden
        a propósito, no se tropieza uno con ellas al bajar a mirar los totales.

        Lo de dentro NO cambia: sigue siendo "o se ofrece el botón, O se dice
        por qué no se puede", que es lo que se arregló el 2026-09-30.
      */}
      {factura !== null && (factura.status === "ISSUED" || factura.status === "DRAFT") ? (
        <details className="mas-acciones">
          <summary>{t.factura.masAcciones}</summary>
      {factura !== null && factura.status === "ISSUED" && factura.rectification === null ? (
        <Tarjeta titulo={t.factura.rectificar}>
          <p className="ayuda-neutra">{t.factura.rectificarDetalle}</p>
          {motivosRectificar.length > 0 ? (
            <ul className="lista-pendientes">
              {motivosRectificar.map((c) => (
                <li key={c}>
                  <span>{t.factura.motivosRectificar[c] ?? t.comun.errorGenerico}</span>
                </li>
              ))}
            </ul>
          ) : null}
          {!rectificando ? (
            <Boton variante="secundario" onClick={() => setRectificando(true)} deshabilitado={ocupado || !hayConexion}>
              {t.factura.rectificar}
            </Boton>
          ) : (
            <>
              {/* El motivo lo ELIGE la persona: la aplicacion no lo deduce del
                  importe (URS §35). Por eso cada opcion cuenta lo que PASO. */}
              <Selector
                id="motivo-rectificativa"
                etiqueta={t.factura.rectificarMotivo}
                valor={motivoRectificativa}
                opciones={MOTIVOS_RECTIFICATIVA.map((v) => ({ valor: v, texto: t.factura.rectificarMotivos[v] ?? v }))}
                onChange={(v) => setMotivoRectificativa(v as MotivoRectificativa)}
              />
              <Selector
                id="modalidad-rectificativa"
                etiqueta={t.factura.rectificarModalidad}
                valor={modalidadRectificativa}
                opciones={MODALIDADES_RECTIFICATIVA.map((v) => ({ valor: v, texto: t.factura.rectificarModalidades[v] ?? v }))}
                onChange={(v) => setModalidadRectificativa(v as ModalidadRectificativa)}
              />
              <p className="ayuda-neutra">{t.factura.rectificarModalidadAyuda}</p>
              {fueraDePlazo ? (
                <Aviso tono="aviso" titulo={t.factura.fueraDePlazoTitulo}>
                  {t.factura.fueraDePlazoDetalle}
                </Aviso>
              ) : null}
              <div className="acciones">
                <Boton onClick={() => void rectificar(fueraDePlazo)} deshabilitado={ocupado || !hayConexion}>
                  {ocupado ? t.factura.rectificando : fueraDePlazo ? t.factura.fueraDePlazoConfirmar : t.factura.rectificarAccion}
                </Boton>
                <Boton
                  variante="secundario"
                  onClick={() => {
                    setRectificando(false);
                    setFueraDePlazo(false);
                    setMotivosRectificar([]);
                  }}
                >
                  {t.presupuesto.cancelarAccion}
                </Boton>
              </div>
            </>
          )}
        </Tarjeta>
      ) : null}

      {factura !== null && factura.status === "ISSUED" ? (
        <Tarjeta titulo={t.factura.anular}>
          <p className="ayuda-neutra">{t.factura.anularDetalle}</p>
          {/*
            O se ofrece el boton, O se dice por que no se puede. Las dos cosas
            a la vez es lo que habia: el aviso aparecia DESPUES de intentarlo
            y el boton seguia invitando a repetir el mismo rechazo.

            Mientras no se sabe (la API no contesto) tampoco se ofrece: anular
            es irreversible y no se empuja a ciegas.
          */}
          {controlAnulacion === null ? (
            <p className="ayuda-neutra">{t.factura.anularSinSaber}</p>
          ) : controlAnulacion.outcome !== "ALLOW_VOID" ? (
            <ul className="lista-pendientes">
              {controlAnulacion.reasons.map((m) => (
                <li key={m.code}>
                  <span>{t.factura.motivosAnular[m.code] ?? t.comun.errorGenerico}</span>
                </li>
              ))}
            </ul>
          ) : !anulando ? (
            <Boton variante="secundario" onClick={() => setAnulando(true)} deshabilitado={ocupado || !hayConexion}>
              {t.factura.anular}
            </Boton>
          ) : (
            <>
              <Aviso tono="aviso">{t.factura.anularEfecto}</Aviso>
              <AreaTexto
                id="motivo-anulacion"
                etiqueta={t.factura.anularMotivo}
                ayuda={t.factura.anularMotivoAyuda}
                valor={motivoAnulacion}
                onChange={setMotivoAnulacion}
                filas={3}
                requerido
              />
              <div className="acciones">
                {/* Anular es irreversible: la confirmación va en rojo, y el texto
                    del botón dice lo que hace (el color nunca va solo, OD-0017 D-04). */}
                <Boton
                  variante="peligro"
                  onClick={() => void anular()}
                  deshabilitado={ocupado || !hayConexion || motivoAnulacion.trim() === ""}
                >
                  {ocupado ? t.factura.anulando : t.factura.confirmarAnular}
                </Boton>
                <Boton
                  variante="secundario"
                  onClick={() => setAnulando(false)}
                >
                  {t.presupuesto.cancelarAccion}
                </Boton>
              </div>
            </>
          )}
        </Tarjeta>
      ) : null}

      {factura !== null && factura.status === "DRAFT" ? (
        <Tarjeta titulo={t.factura.descartar}>
          <p className="ayuda-neutra">{t.factura.descartarDetalle}</p>
          {!descartando ? (
            <Boton variante="secundario" onClick={() => setDescartando(true)} deshabilitado={ocupado || !hayConexion}>
              {t.factura.descartar}
            </Boton>
          ) : (
            <div className="acciones">
              <Boton variante="peligro" onClick={() => void descartar()} deshabilitado={ocupado || !hayConexion}>
                {t.factura.confirmarDescartar}
              </Boton>
              <Boton variante="secundario" onClick={() => setDescartando(false)}>
                {t.presupuesto.cancelarAccion}
              </Boton>
            </div>
          )}
        </Tarjeta>
      ) : null}
        </details>
      ) : null}
    </>
  );
}
